import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Result, Button, Spin, Card } from 'antd';
import { CheckCircleOutlined, HomeOutlined } from '@ant-design/icons';
import { apiService } from '../../services/api';
import Layout from '../../components/Common/Layout/Layout';
import { firstQueryParam } from '../../utils/paymentQueryParams';
import { useAuth } from '../../contexts/AuthContext';
import { companyProfileQueryKeys, menteeProfileQueryKeys, mentorProfileQueryKeys, queryClient } from '../../lib/queryClient';
import { isSubscriptionUiActive } from '../../utils/subscriptionUiActive';

const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { syncAuthAfterSubscription } = useAuth();
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string>('');

  const getRolePanelPath = () => {
    const storedUserRaw = localStorage.getItem('user');
    const storedRole = localStorage.getItem('userRole');
    let role = storedRole;

    if (storedUserRaw) {
      try {
        const parsed = JSON.parse(storedUserRaw);
        role = parsed?.role || role;
      } catch {
        // parse edilemezse fallback olarak userRole kullanılacak
      }
    }

    if (role === 'Mentor') return '/profile';
    if (role === 'Mentee') return '/mentee-profile';
    if (role === 'Company') return '/company-panel';
    return '/';
  };

  const invalidateSubscriptionCaches = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.profile }),
      queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.subscription }),
      queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.subscriptionHistory }),
      queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.profile }),
      queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.subscription }),
      queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.subscriptionHistory }),
      queryClient.invalidateQueries({ queryKey: companyProfileQueryKeys.profile }),
      queryClient.invalidateQueries({ queryKey: companyProfileQueryKeys.subscription }),
      queryClient.invalidateQueries({ queryKey: companyProfileQueryKeys.subscriptionHistory }),
    ]);
  };

  const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const refreshSubscriptionStateWithRetry = async (): Promise<boolean> => {
    const maxAttempts = 8;
    const retryDelayMs = 800;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        await syncAuthAfterSubscription();

        const currentSubscription = await apiService.getCurrentSubscription();
        const backendActive = !!(
          currentSubscription?.success &&
          currentSubscription?.data?.isActive &&
          !currentSubscription?.data?.isExpired
        );

        if (backendActive) {
          await syncAuthAfterSubscription();
          return true;
        }

        const raw = localStorage.getItem('user');
        const u = raw ? JSON.parse(raw) : null;
        if (isSubscriptionUiActive(u)) return true;
      } catch {
        // retry
      }

      if (attempt < maxAttempts) {
        await wait(retryDelayMs);
      }
    }

    return false;
  };

  useEffect(() => {
    console.log('PaymentSuccess: Component mounted, useEffect triggered');
    console.log('PaymentSuccess: Current URL', window.location.href);
    console.log('PaymentSuccess: Search params object', searchParams);
    console.log('PaymentSuccess: All search params', Object.fromEntries(searchParams.entries()));
    
    const processPayment = async () => {
      try {
        setLoading(true);
        
        // URL parametrelerini al
        const siparisId = firstQueryParam(searchParams, ['Siparis_ID', 'siparis_id', 'SIPARIS_ID']);
        const sonuc = firstQueryParam(searchParams, ['Sonuc', 'sonuc', 'SONUC']);
        const sonucStr = firstQueryParam(searchParams, ['Sonuc_Str', 'sonuc_str', 'SONUC_STR', 'SonucStr', 'sonucstr']);
        const islemId = firstQueryParam(searchParams, ['Islem_ID', 'islem_id', 'ISLEM_ID']);
        const hash = firstQueryParam(searchParams, ['Hash', 'hash', 'HASH']);
        
        // Debug: Tüm URL parametrelerini logla
        console.log('PaymentSuccess: URL params extracted', {
          siparisId,
          sonuc,
          sonucStr,
          islemId,
          hash,
          allParams: Object.fromEntries(searchParams.entries()),
          fullUrl: window.location.href
        });

        // localStorage'dan OrderId'yi al
        const storedOrderId = localStorage.getItem('pendingPaymentOrderId');
        const storedAdvertisementOrderId = localStorage.getItem('pendingAdvertisementPaymentOrderId');
        const orderIdToUse = siparisId || storedOrderId || storedAdvertisementOrderId;

        // Sipariş ID formatına göre ödeme tipini belirle (ADV-* reklam, SUB-/RENEW-* abonelik)
        const inferredPaymentType = (orderIdToUse || '').startsWith('ADV-') ? 'advertisement' : 'subscription';
        const isAdvertisementPayment = inferredPaymentType === 'advertisement';
        
        console.log('PaymentSuccess: OrderId sources', { 
          urlParam: siparisId, 
          stored: storedOrderId, 
          using: orderIdToUse 
        });
        
        // Backend callback endpoint'ine bilgi gönder (backend işlemi yapacak)
        if (orderIdToUse) {
          console.log('PaymentSuccess: Processing payment callback', { orderId: orderIdToUse, sonuc: sonuc || '1', sonucStr });
          
          try {
            // Backend callback endpoint'ine GET request gönder (fromFrontend=true ile)
            // apiService kullanarak CORS sorununu önle
            const params: {
              Siparis_ID: string;
              Sonuc: string;
              Sonuc_Str?: string;
              Islem_ID?: string;
              Hash?: string;
              fromFrontend: boolean;
            } = {
              Siparis_ID: orderIdToUse,
              Sonuc: sonuc || '1',
              fromFrontend: true
            };
            
            if (sonucStr) params.Sonuc_Str = sonucStr;
            if (islemId) params.Islem_ID = islemId;
            if (hash) params.Hash = hash;
            
            console.log('PaymentSuccess: Calling backend callback with params', params);
            
            // Reklam ödemesi mi yoksa abonelik ödemesi mi?
            let result;
            if (isAdvertisementPayment) {
              // Reklam ödeme callback'i
              const advParams = new URLSearchParams({
                Siparis_ID: orderIdToUse,
                Sonuc: sonuc || '1',
                Sonuc_Str: sonucStr || 'Başarılı',
                fromFrontend: 'true'
              });
              if (islemId) advParams.append('Islem_ID', islemId);
              if (hash) advParams.append('Hash', hash);
              
              const advResponse = await fetch(`/api/mentor/advertisements/payment/success?${advParams.toString()}`);
              result = await advResponse.json();
            } else {
              // Abonelik ödeme callback'i
              result = await apiService.processPaymentCallback(params);
            }
            
            console.log('PaymentSuccess: Backend callback result', result);
            
            if (result.success) {
                setMessage(result.message || sonucStr || (isAdvertisementPayment ? 'Ödeme başarılı. Reklam talebiniz admin onayı bekliyor.' : 'Ödeme başarıyla tamamlandı! Aboneliğiniz aktif edildi.'));
                
                if (isAdvertisementPayment) {
                  localStorage.removeItem('pendingAdvertisementPaymentOrderId');
                  localStorage.removeItem('pendingPaymentOrderId');
                  // Reklam ödemesinde token yenileme - kullanıcı değişmesin
                  setTimeout(() => {
                    navigate('/profile?tab=advertisements');
                  }, 2000);
                } else {
                  localStorage.removeItem('pendingPaymentOrderId');
                  localStorage.removeItem('pendingAdvertisementPaymentOrderId');
                  // Abonelik durumunu deterministik güncelle ve panel cache'lerini tazele
                  let isSynced = false;
                  try {
                    isSynced = await refreshSubscriptionStateWithRetry();
                  } catch {
                    isSynced = false;
                  }

                  await invalidateSubscriptionCaches();

                  const panelPath = getRolePanelPath();
                  const targetPath = isSynced ? panelPath : `${panelPath}?tab=subscription`;
                  setTimeout(() => { navigate(targetPath); }, 1500);
                }
              } else {
                console.error('PaymentSuccess: Backend callback returned success=false', result);
                setMessage(result.message || sonucStr || 'Ödeme işlemi tamamlandı ancak bir sorun oluştu. Lütfen destek ekibi ile iletişime geçin.');
              }
          } catch (error: any) {
            console.error('PaymentSuccess: Payment callback error', error);
            console.error('PaymentSuccess: Error details', { 
              message: error?.message, 
              response: error?.response?.data,
              status: error?.response?.status,
              orderId: orderIdToUse 
            });
            
            const errorMessage = error?.response?.data?.message || error?.message || 'Bilinmeyen hata';
            if (sonuc === '1' || !sonuc) {
              setMessage(isAdvertisementPayment
                ? `Ödeme alındı ancak kayıt güncellenemedi. Hata: ${errorMessage}. Sipariş ID: ${orderIdToUse}. Lütfen destek ile iletişime geçin.`
                : `Ödeme başarıyla tamamlandı ancak abonelik durumu güncellenemedi. Hata: ${errorMessage}. Sipariş ID: ${orderIdToUse}. Lütfen destek ekibi ile iletişime geçin.`);
            } else {
              setMessage(sonucStr || errorMessage || 'Ödeme işlemi tamamlandı ancak bir sorun oluştu.');
            }
          }
        } else {
          // Sipariş ID yok (URL ve localStorage'da) - backend çağırma ve token yenileme (kullanıcı değişmesin)
          console.warn('PaymentSuccess: No order ID in URL or localStorage');
          setMessage('Ödeme tamamlandı. Reklam veya abonelik talebiniz işleme alınacaktır. Sorun yaşarsanız destek ile iletişime geçin.');
        }
      } catch (error) {
        console.error('Payment processing error:', error);
        setMessage('Ödeme işlemi tamamlandı ancak bir sorun oluştu.');
      } finally {
        setLoading(false);
      }
    };

    processPayment();
  }, [searchParams, syncAuthAfterSubscription]);

  return (
    <Layout>
      <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <Card style={{ maxWidth: 600, width: '100%' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <p style={{ marginTop: '16px', color: '#666' }}>Ödeme işlemi kontrol ediliyor...</p>
            </div>
          ) : (
            <Result
              status="success"
              icon={<CheckCircleOutlined style={{ color: 'var(--success)', fontSize: '72px' }} />}
              title="Ödeme Başarılı!"
              subTitle={message || 'Aboneliğiniz başarıyla oluşturuldu. Artık tüm özelliklere erişebilirsiniz.'}
              extra={[
                <Button
                  type="primary"
                  key="home"
                  icon={<HomeOutlined />}
                  onClick={() => navigate('/')}
                  size="large"
                >
                  Ana Sayfaya Dön
                </Button>,
                <Button
                  key="panel"
                  onClick={() => {
                    const role = localStorage.getItem('userRole');
                    if (role === 'Mentor') {
                      navigate('/profile');
                    } else if (role === 'Mentee') {
                      navigate('/mentee-profile');
                    } else if (role === 'Company') {
                      navigate('/company-panel');
                    } else {
                      navigate('/');
                    }
                  }}
                  size="large"
                >
                  Panele Git
                </Button>
              ]}
            />
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default PaymentSuccess;

